import { useCallback } from 'react';

import { useRestoreDealItemsHistoryMutation } from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import {
  type UndoEntryItem,
  flushStackIfTopInvalid,
  redoStackState,
  swapEntry,
  undoMutationPendingState,
  undoStackState,
} from '@/states/undo-stack.state';
import { useAtomValue, useSetAtom } from 'jotai';

/** Aligné sur le code renvoyé par l'API quand l'historique ne correspond plus au serveur. */
const DOMAIN_EXCEPTION_RESTORE_CONFLICT = 'restoreConflict';

export const useUndoDealItems = (dealId: string) => {
  const undoStack = useAtomValue(undoStackState);
  const setUndoStack = useSetAtom(undoStackState);
  const redoStack = useAtomValue(redoStackState);
  const setRedoStack = useSetAtom(redoStackState);
  const setUndoPending = useSetAtom(undoMutationPendingState);
  const [restoreMutation, { loading }] = useRestoreDealItemsHistoryMutation();

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  const pushUndoEntry = useCallback(
    (items: UndoEntryItem[]) => {
      if (items.length === 0) return;
      setUndoStack((prev) => [...prev, { items }]);
      // Nouvelle action utilisateur → vider le redo (comportement éditeur standard)
      setRedoStack([]);
    },
    [setUndoStack, setRedoStack]
  );

  const undo = useCallback(async (): Promise<boolean> => {
    setUndoPending(true);
    try {
      if (undoStack.length === 0) return false;
      const lastEntry = undoStack[undoStack.length - 1];
      trackEvent('UNDO_TRIGGERED', {
        dealId,
        stackDepth: undoStack.length,
      });

      const result = await restoreMutation({
        variables: {
          dealId,
          items: lastEntry.items.map((item) => ({
            dealItemId: item.dealItemId,
            previousHistoryId: item.previousHistoryId,
            currentHistoryId: item.currentHistoryId,
          })),
        },
      });

      const response = result.data?.restoreDealItemsHistory;
      if (response?.__typename === 'DomainException') {
        if (response.code === DOMAIN_EXCEPTION_RESTORE_CONFLICT) {
          setUndoStack([]);
          setRedoStack([]);
        }
        return false;
      }

      setUndoStack((prev) => prev.slice(0, -1));
      setRedoStack((prev) => [...prev, swapEntry(lastEntry)]);
      trackEvent('UNDO_COMPLETED', {
        dealId,
        itemCount: lastEntry.items.length,
      });
      return true;
    } finally {
      setUndoPending(false);
    }
  }, [
    undoStack,
    restoreMutation,
    dealId,
    setUndoStack,
    setRedoStack,
    setUndoPending,
  ]);

  const redo = useCallback(async (): Promise<boolean> => {
    setUndoPending(true);
    try {
      if (redoStack.length === 0) return false;
      const lastEntry = redoStack[redoStack.length - 1];
      trackEvent('REDO_TRIGGERED', {
        dealId,
        stackDepth: redoStack.length,
      });

      const result = await restoreMutation({
        variables: {
          dealId,
          items: lastEntry.items.map((item) => ({
            dealItemId: item.dealItemId,
            previousHistoryId: item.previousHistoryId,
            currentHistoryId: item.currentHistoryId,
          })),
        },
      });

      const response = result.data?.restoreDealItemsHistory;
      if (response?.__typename === 'DomainException') {
        if (response.code === DOMAIN_EXCEPTION_RESTORE_CONFLICT) {
          setUndoStack([]);
          setRedoStack([]);
        }
        return false;
      }

      setRedoStack((prev) => prev.slice(0, -1));
      setUndoStack((prev) => [...prev, swapEntry(lastEntry)]);
      trackEvent('REDO_COMPLETED', {
        dealId,
        itemCount: lastEntry.items.length,
      });
      return true;
    } finally {
      setUndoPending(false);
    }
  }, [
    redoStack,
    restoreMutation,
    dealId,
    setUndoStack,
    setRedoStack,
    setUndoPending,
  ]);

  const invalidateUndoForItems = useCallback(
    (currentHistoryIds: Map<string, string>) => {
      setUndoStack((prev) => flushStackIfTopInvalid(prev, currentHistoryIds));
      setRedoStack((prev) => flushStackIfTopInvalid(prev, currentHistoryIds));
    },
    [setUndoStack, setRedoStack]
  );

  return {
    canUndo,
    canRedo,
    pushUndoEntry,
    undo,
    redo,
    invalidateUndoForItems,
    loading,
  };
};
